/**
 * `SwapCreditModal` (#15) — reassign which credit source pays for ONE booking.
 * Dispatches the `swap_credit` canonical action, which in one atomic tx returns
 * a session to the booking's current source and consumes one from the chosen
 * new source, then repoints the booking. The member's packages + memberships
 * are threaded down from the already-loaded member detail (no extra fetch); the
 * booking's CURRENT source is excluded from the target list.
 */

import { useMemo, useState } from "react";
import type {
  MemberDetailMembership,
  MemberDetailPackage,
} from "../lib/api";
import { dispatchAction } from "../lib/dispatch";

interface SwapTarget {
  readonly kind: "package" | "membership";
  readonly id: string;
  readonly label: string;
  readonly detail: string;
}

export function SwapCreditModal({
  bookingId,
  currentCreditSource,
  currentSourceId,
  packages,
  memberships,
  onDone,
}: {
  readonly bookingId: string;
  readonly currentCreditSource: "package" | "membership" | "unlimited" | null;
  readonly currentSourceId: string | null;
  readonly packages: ReadonlyArray<MemberDetailPackage>;
  readonly memberships: ReadonlyArray<MemberDetailMembership>;
  readonly onDone: () => void;
}): JSX.Element {
  // Eligible targets: active package with remaining > 0, or active-lifecycle,
  // non-paused membership (unlimited or with remaining > 0). Never the source
  // the booking is already on.
  const targets = useMemo<ReadonlyArray<SwapTarget>>(() => {
    const out: SwapTarget[] = [];
    for (const p of packages) {
      if (p.status !== "active" || p.remaining_sessions <= 0) continue;
      if (currentSourceId === p.id) continue;
      out.push({
        kind: "package",
        id: p.id,
        label: p.label,
        detail: `${p.remaining_sessions} left`,
      });
    }
    for (const m of memberships) {
      if (m.lifecycle_state !== "active" || m.status !== "active") continue;
      if (m.paused) continue;
      if (m.remaining_sessions !== null && m.remaining_sessions <= 0) continue;
      if (currentSourceId === m.id) continue;
      out.push({
        kind: "membership",
        id: m.id,
        label: m.membership_slug,
        detail:
          m.remaining_sessions === null
            ? "unlimited"
            : `${m.remaining_sessions} left`,
      });
    }
    return out;
  }, [packages, memberships, currentSourceId]);

  const currentLabel = useMemo(() => {
    if (currentSourceId === null) return "no credit source";
    const p = packages.find((x) => x.id === currentSourceId);
    if (p) return `${p.label} (package)`;
    const m = memberships.find((x) => x.id === currentSourceId);
    if (m)
      return `${m.membership_slug} (${
        currentCreditSource === "unlimited" ? "unlimited membership" : "membership"
      })`;
    return currentCreditSource ?? "unknown";
  }, [currentSourceId, currentCreditSource, packages, memberships]);

  const [choice, setChoice] = useState(
    targets[0] ? `${targets[0].kind}:${targets[0].id}` : "",
  );
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (choice === "") {
      setError("Pick a source to move this session onto.");
      return;
    }
    const sep = choice.indexOf(":");
    const kind = choice.slice(0, sep) as "package" | "membership";
    const id = choice.slice(sep + 1);
    setBusy(true);
    setError(null);
    try {
      const r = await dispatchAction("swap_credit", {
        booking_id: bookingId,
        to_source_kind: kind,
        to_source_id: id,
        ...(note.trim() !== "" ? { note: note.trim() } : {}),
      });
      setBusy(false);
      if (!r.ok) {
        setError(`Swap failed: ${r.message || r.code}`);
        return;
      }
      setDone("Credit source swapped.");
      onDone();
    } catch (e) {
      setBusy(false);
      setError((e as Error).message);
    }
  };

  if (done !== null) {
    return <p className="xfer-done">✅ {done}</p>;
  }

  if (targets.length === 0) {
    return (
      <p className="xfer-empty">
        No other active credit source to move this session onto. Currently on{" "}
        {currentLabel}.
      </p>
    );
  }

  return (
    <div className="xfer-modal">
      <p className="xfer-hint">
        Currently covered by <strong>{currentLabel}</strong>. Moving this session
        returns one credit to it and takes one from the source you pick.
      </p>

      <label className="xfer-field">
        <span>Move onto</span>
        <select value={choice} onChange={(e) => setChoice(e.target.value)}>
          {targets.map((t) => (
            <option key={`${t.kind}:${t.id}`} value={`${t.kind}:${t.id}`}>
              {t.label} · {t.detail} ({t.kind})
            </option>
          ))}
        </select>
      </label>

      <label className="xfer-field">
        <span>Note (optional)</span>
        <input
          type="text"
          maxLength={500}
          placeholder="e.g. booked against the wrong package"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className="xfer-actions">
        <button
          type="button"
          className="xfer-submit"
          disabled={busy || choice === ""}
          onClick={() => void submit()}
        >
          {busy ? "Swapping…" : "Swap credit"}
        </button>
      </div>
      {error !== null ? <p className="cal-action-error">{error}</p> : null}
    </div>
  );
}
